import { useState, useEffect, useRef, useCallback } from "react";
import { TextInput } from "react-native";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useFocusEffect, useRoute, RouteProp } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import {
  AttachmentType,
  StoragePathType,
  useProcessAttachmentMutation,
  useGetFileQuery,
} from "@axio/graphql/generated/graphql";
import type { LlmStreamContentBlock } from "@axio/graphql/generated/graphql";
import type { ComposerAttachment } from "../../../components/chatNewInterface/chatLayout/messageComposer/ChatComposerBar";
import { usePostHog } from "posthog-js/react";
import type { LearningLlmStartStream } from "../../../hooks/useLearningLlmStream";
import useChatSpeechInput, { appendTranscriptToDraft } from "../../../hooks/useChatSpeechInput";
import Sentry from "../../../utils/sentry";
import useFileUpload, { ProcessedFile } from "../../../hooks/useFileUpload";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import {
  TOOL_OPTIONS_BY_CHAT_TYPE,
  type ToolId,
} from "../../../components/chatNewInterface/interfaceComponents/ToolsMenu/toolOptions";
import ChatComposerBar from "../../../components/chatNewInterface/chatLayout/messageComposer/ChatComposerBar";
import { useMathVirtualKeyboardContext } from "../../../contexts/MathVirtualKeyboardContext";
import { getMathModePreference, saveMathModePreference } from "../../../utils/mathModeStorage";
import { type PlaybackSpeed } from "../../../utils/toolPreferenceStorage";
import { useChatAudioPreferences } from "../../../contexts/ChatAudioPreferencesContext";

const viewsWithoutAttachments = ["Onboarding", "Custom Resource Student"];
const VOICE_INPUT_ERROR_TIMEOUT_MS = 3000;

interface AITutorChatBarContainerProps {
  onSubmit?: () => void;
  chatId?: string | null;
  contentNodeId: string;
  startStream: LearningLlmStartStream;
  isStreaming: boolean;
  onStreamComplete?: (
    chatId?: string,
    serverMessageId?: string,
    blocks?: LlmStreamContentBlock[],
    metadata?: Record<string, unknown>
  ) => void;
  onStreamError?: (payload: { title: string; message: string; onRetry?: () => void }) => void;
  onStreamChunk?: (payload: { text: string; isFinalChunk?: boolean }) => void;
  onAudioToggleChange?: (enabled: boolean) => void;
  onPlaybackSpeedChange?: (speed: PlaybackSpeed) => void;
  onObjectiveCompleted?: (objectiveIds: string[]) => void;
  onAllObjectivesCompleted?: () => void;
  onStreamStart?: (message: string, attachment?: ComposerAttachment | null, meta?: { isAutoStart?: boolean }) => void;
  onToolResult?: (payload: { tool: string; result: Record<string, unknown> }) => void;
  initialMessage?: string;
  autoStart?: boolean;
}

function AITutorChatBarContainer({
  onSubmit = () => {},
  chatId,
  contentNodeId,
  startStream,
  isStreaming,
  onStreamComplete,
  onStreamError,
  onStreamChunk,
  onAudioToggleChange,
  onPlaybackSpeedChange,
  onObjectiveCompleted,
  onAllObjectivesCompleted,
  onStreamStart,
  onToolResult,
  initialMessage,
  autoStart,
}: AITutorChatBarContainerProps) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { isAudioOn, playbackSpeed, setIsAudioOn, setPlaybackSpeed } = useChatAudioPreferences();

  const { state, dispatch } = useAppState();

  const route = useRoute<RouteProp<RootStackParamList, keyof RootStackParamList>>();

  const {
    data: fileData,
    startPolling,
    stopPolling,
    refetch,
  } = useGetFileQuery({ variables: { fileId: "" }, fetchPolicy: "network-only" });

  const chatInputRef = useRef<TextInput>(null);

  const [inputValue, setInputValue] = useState("");
  const [attachment, setAttachment] = useState<ComposerAttachment | null>(null);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(chatId ?? null);
  const [activeToolId, setActiveToolId] = useState<ToolId | null>(null);
  const [isMathModeEnabled, setIsMathModeEnabled] = useState(false);
  const [voiceInputErrorMessage, setVoiceInputErrorMessage] = useState<string | null>(null);
  const vkContext = useMathVirtualKeyboardContext();

  useEffect(() => {
    getMathModePreference().then(setIsMathModeEnabled);
  }, []);

  useEffect(() => {
    onPlaybackSpeedChange?.(playbackSpeed);
  }, [onPlaybackSpeedChange, playbackSpeed]);

  useEffect(() => {
    if (isMathModeEnabled || !vkContext) {
      return;
    }

    vkContext.setVirtualKeyboardVisible(false);
  }, [isMathModeEnabled, vkContext]);

  const [processAttachment] = useProcessAttachmentMutation();

  const isAttachmentEnabled = !viewsWithoutAttachments.includes(route.name);
  const hasAutoStartedRef = useRef(false);

  useEffect(() => {
    setActiveChatId(chatId ?? null);
  }, [chatId]);

  useFocusEffect(
    useCallback(() => {
      const handleDocumentKeyPress = (event: KeyboardEvent) => {
        const composedPath = typeof event.composedPath === "function" ? event.composedPath() : [];
        const isTypingInMathField = composedPath.some(
          (node) => node instanceof HTMLElement && node.tagName?.toLowerCase() === "math-field"
        );

        if (isMathModeEnabled || isTypingInMathField) {
          return;
        }

        // Don't steal focus if the user is already typing in another input/textarea
        const active = document.activeElement;
        if (active && active !== chatInputRef.current) {
          const tag = (active as HTMLElement).tagName?.toLowerCase();
          if (tag === "input" || tag === "textarea" || (active as HTMLElement).isContentEditable) {
            return;
          }
        }

        chatInputRef.current?.focus();
      };

      if (state.meta.keypressIsListening) {
        document.addEventListener("keypress", handleDocumentKeyPress);
      } else {
        document.removeEventListener("keypress", handleDocumentKeyPress);
      }

      return () => {
        document.removeEventListener("keypress", handleDocumentKeyPress);
      };
    }, [isMathModeEnabled, state.meta.keypressIsListening])
  );

  useEffect(() => {
    if (fileData?.getFile?.data?.length > 0) {
      const file = fileData.getFile.data[0];
      const isLoading = file.status !== "completed";
      if (!isLoading) {
        setIsAttachmentLoading(false);
        stopPolling();
      }
    }
  }, [fileData, stopPolling]);

  useEffect(() => {
    if (!autoStart || hasAutoStartedRef.current) {
      return;
    }
    if (!initialMessage?.trim()) {
      return;
    }
    hasAutoStartedRef.current = true;
    void sendMessageToAxio(initialMessage, null, { isAutoStart: true });
  }, [autoStart, initialMessage]);

  useEffect(() => {
    if (!voiceInputErrorMessage) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setVoiceInputErrorMessage(null);
    }, VOICE_INPUT_ERROR_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [voiceInputErrorMessage]);

  const handleSubmit = (message: string) => {
    void handleSubmitEditing(message);
    onSubmit();
  };

  const handleChangeText = (text: string) => {
    setVoiceInputErrorMessage(null);
    setInputValue(text);
  };

  const handleBeforeUpload = () => {
    setIsAttachmentLoading(true);
  };

  const handleAfterUpload = async (processedFilesArray: ProcessedFile[]) => {
    for (const processedFile of processedFilesArray) {
      await processAttachment({
        variables: {
          fileId: processedFile.file.id,
        },
      });
      setAttachment({
        id: processedFile.file.id,
        type: "file",
        name: processedFile.file.fileName,
      });
      refetch({ fileId: processedFile.file.id });
    }

    startPolling(3000);
  };

  const handleFileUploadError = () => {
    setIsAttachmentLoading(false);
    setAttachment(null);
  };

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.ChatAttachments,
    beforeUpload: handleBeforeUpload,
    afterUpload: handleAfterUpload,
    onError: handleFileUploadError,
    multiple: true,
  });

  const onAttachmentPress = useCallback(async () => {
    const files = await handleFileUpload();
    if (!files || files.length === 0) {
      setIsAttachmentLoading(false);
    }
  }, [handleFileUpload]);

  const handleDeleteAttachment = () => {
    setAttachment(null);
  };

  const handleSubmitEditing = async (quickItemValue?: string) => {
    try {
      if (isAttachmentLoading || isStreaming) {
        return;
      }

      posthog?.capture("Message sent to axio");
      setAttachment(null);
      setVoiceInputErrorMessage(null);

      if (state.meta.isAdventure) {
        dispatch({
          type: actions.SET_META,
          payload: { isAdventure: false },
        });
      }

      setInputValue("");
      await sendMessageToAxio(quickItemValue);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const sendMessageToAxio = async (
    quickItemValue?: string,
    attachmentOverride?: ComposerAttachment | null,
    options?: { isAutoStart?: boolean }
  ) => {
    if (isStreaming) {
      return;
    }

    const message = (quickItemValue || inputValue || "").trim();
    if (!message) {
      return;
    }

    const activeAttachment = attachmentOverride ?? attachment;

    onStreamStart?.(message, activeAttachment, {
      isAutoStart: options?.isAutoStart,
    });

    const attachmentInput = activeAttachment
      ? {
          type: AttachmentType.Document,
          id: activeAttachment.id,
          fileId: activeAttachment.id,
        }
      : undefined;

    await startStream(message, {
      chatId: activeChatId ?? undefined,
      contentNodeId,
      attachment: attachmentInput,
      onChunk: ({ text, isFinalChunk }) => {
        onStreamChunk?.({ text, isFinalChunk });
      },
      onChatId: (nextChatId) => {
        setActiveChatId(nextChatId);
      },
      onObjectiveCompleted: ({ objectiveIds }) => {
        onObjectiveCompleted?.(objectiveIds);
      },
      onAllObjectivesCompleted: () => {
        onAllObjectivesCompleted?.();
      },
      onToolResult,
      onComplete: ({ event }) => {
        if (event?.chatId) {
          setActiveChatId(event.chatId);
        }
        onStreamComplete?.(event?.chatId, event?.messageId, event?.blocks, event?.metadata);
      },
      onError: ({ error }) => {
        Sentry.captureException(error);
        onStreamError?.({
          title: "Failed to send message",
          message: error.message || "An error occurred while sending your message. Please try again.",
          onRetry: async () => {
            await sendMessageToAxio(message, activeAttachment, options);
          },
        });
      },
    });
  };

  const handleTranscriptReady = useCallback((transcript: string) => {
    setVoiceInputErrorMessage(null);
    setInputValue((currentValue) => appendTranscriptToDraft(currentValue, transcript));
    setTimeout(() => {
      chatInputRef.current?.focus();
    }, 0);
  }, []);

  const handleVoiceInputError = useCallback(() => {
    setVoiceInputErrorMessage(t("Something went wrong with dictation. Try again."));
  }, [t]);

  const {
    phase: voiceInputPhase,
    audioLevel,
    handleRecordingCancelPress,
    handleRecordingStopPress,
    handleRecordingStartPress,
  } = useChatSpeechInput({
    isDisabled: isStreaming || isAttachmentLoading,
    onTranscriptReady: handleTranscriptReady,
    onError: handleVoiceInputError,
  });

  const handleRecordingStart = useCallback(() => {
    setVoiceInputErrorMessage(null);
    handleRecordingStartPress();
  }, [handleRecordingStartPress]);

  const handleRecordingCancel = useCallback(() => {
    setVoiceInputErrorMessage(null);
    handleRecordingCancelPress();
  }, [handleRecordingCancelPress]);

  const onAudioOffPress = () => {
    setIsAudioOn(false);
    onAudioToggleChange?.(false);
  };

  const onAudioOnPress = () => {
    setIsAudioOn(true);
    onAudioToggleChange?.(true);
  };

  const handlePlaybackSpeedChange = useCallback(
    (speed: PlaybackSpeed) => {
      setPlaybackSpeed(speed);
      onPlaybackSpeedChange?.(speed);
    },
    [onPlaybackSpeedChange, setPlaybackSpeed]
  );

  const handleToolSelect = useCallback(
    (toolId: ToolId) => {
      const nextToolId = activeToolId === toolId ? null : toolId;
      setActiveToolId(nextToolId);
    },
    [activeToolId]
  );

  const handleMathModeToggle = useCallback((enabled: boolean) => {
    setIsMathModeEnabled(enabled);
    saveMathModePreference(enabled);
  }, []);

  const placeholderText = t("Type your response or ask a question...");

  return (
    <ChatComposerBar
      value={inputValue}
      voiceInputPhase={voiceInputPhase}
      voiceInputErrorMessage={voiceInputErrorMessage}
      audioLevel={audioLevel}
      isAudioOn={isAudioOn}
      placeholder={placeholderText}
      inputAccessibilityLabel="Type your response to the AI tutor"
      inputRef={chatInputRef}
      onChangeText={handleChangeText}
      onSubmitEditing={handleSubmit}
      isDisabled={isStreaming || isAttachmentLoading}
      onAudioOffPress={onAudioOffPress}
      onAudioOnPress={onAudioOnPress}
      chatContext="ile"
      tools={TOOL_OPTIONS_BY_CHAT_TYPE.ile}
      activeToolId={activeToolId}
      onToolSelect={handleToolSelect}
      mathModeEnabled={isMathModeEnabled}
      onMathModeToggle={handleMathModeToggle}
      playbackSpeed={playbackSpeed}
      onPlaybackSpeedChange={handlePlaybackSpeedChange}
      onRecordingStopPress={handleRecordingStopPress}
      onRecordingStartPress={handleRecordingStart}
      onRecordingCancelPress={handleRecordingCancel}
      isAttachmentEnabled={isAttachmentEnabled}
      attachment={attachment}
      isAttachmentLoading={isAttachmentLoading}
      onAttachmentPress={onAttachmentPress}
      onDeleteAttachmentPress={handleDeleteAttachment}
    />
  );
}

export default AITutorChatBarContainer;
